import { PropsWithChildren, useRef, useState } from 'react'
import css from './mainLayout.module.css'
import LiveClock from './components/LiveClock'
import Button from '../../Buttons/Button'
import ArrowLeft from '../../../assets/icons/arrowLeft.svg?react'
import LogoTextSvg from '../../../assets/images/logoText.svg?react'
import LogoSquareSvg from '../../../assets/images/logoSquare.svg?react'
import DashboardsIcon from '../../../assets/icons/dashboards.svg?react'
import HomeIcon from '../../../assets/icons/home.svg?react'
import AIIcon from '../../../assets/icons/ai-icon.svg?react'
import SystemEventsIcon from '../../../assets/icons/system-events.svg?react'
import EarthIcon from '../../../assets/icons/earth.svg?react'
import CalendarIcon from '../../../assets/icons/calendar.svg?react'
import SettingsIcon from '../../../assets/icons/settings.svg?react'
import MyOrganizationIcon from '../../../assets/icons/myOrganization.svg?react'
import { useRecoilState, useRecoilValue } from 'recoil'
import NavigationItem from './components/NavigationItem'
import urls from '../../../navigation/urls.ts'
import mainLayoutAtom from './mainLayout.atom.ts'
import WSConnectionIndicator from '@/services/ws/WSConnectionIndicator'
import NotificationCenter from '@/services/Notification/NotificationCenter/index.tsx'
import usePopup from '@/shared/hooks/usePopup.ts'
import ProfileIcon from '@/shared/Layouts/MainLayout/components/ProfileIcon'
import NotificationsButton from '@/services/Notification/components/NotificationsButton/index.tsx'
import AccountSettingsSidePage from '@/pages/User/AccountSettingsSidePage/index.tsx'
import userAtom from '@/pages/User/user.atom.ts'
import useSelfOrganization from '@/pages/MyOrganization/hooks/useSelfOrganization.ts'
import config from '@/config.ts'
import useAccessToken from '@/pages/Auth/hooks/useAccessToken.ts'
import DotIcon from '@/assets/icons/circle-fill.svg?react'

const MainLayout = (props: PropsWithChildren) => {
  const accessToken = useAccessToken()

  const [state, setState] = useRecoilState(mainLayoutAtom)
  const ref = useRef<HTMLDivElement>(null)
  const { open, setOpen } = usePopup(ref)
  const [openAccountSettingsSidePage, setOpenAccountSettingsSidePage] = useState(false)

  const userInfo = useRecoilValue(userAtom)
  const isAdmin = userInfo.isAdmin || userInfo.isOrganizationAdmin
  const selfOrganization = useSelfOrganization()

  return (
    <div className={css.wrapper}>
      <div className={css.header}>
        <div className={css.headerShadow} />
        <div className="inline-flex-gap gap16 center">
          <div className="inline-flex-gap gap12 center" style={{ paddingLeft: '12px' }}>
            <LogoSquareSvg />
            <div className="inline-flex-gap gap8 center">
              <LogoTextSvg />
              {selfOrganization && selfOrganization.image && (
                <>
                  <DotIcon style={{ width: '6px', height: '6px', color: '#0000000F' }} />
                  <img
                    key={selfOrganization.id}
                    alt={selfOrganization.name}
                    src={`${config.API_URL}/nsi/v1/organizations/${selfOrganization.id}/image?accessToken=${accessToken}`}
                    style={{ height: '20px' }}
                  />
                </>
              )}
            </div>
          </div>
          <WSConnectionIndicator />
        </div>
        <div className={css.headerControls}>
          <div ref={ref}>
            <NotificationsButton setOpen={setOpen} />
            <div style={{ position: 'relative' }}>
              <NotificationCenter open={open} />
            </div>
          </div>
          <ProfileIcon onOpenAccountSettingsSidePage={() => setOpenAccountSettingsSidePage(true)} />
        </div>
      </div>
      <div className={css.contentWrapper}>
        <nav className={css.nav} data-open={state.navigationOpen}>
          <div className={css.navShadow} />
          <div className="flex-lines" style={{ gap: '2px' }}>
            <NavigationItem
              matchPrefixes={[]}
              minified={!state.navigationOpen}
              icon={<HomeIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />}
              title="Главная"
              navItems={[{ title: 'Главная', to: urls.main }]}
            />
            <NavigationItem
              matchPrefixes={['/equipment']}
              minified={!state.navigationOpen}
              icon={<EarthIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />}
              title="Лифты"
              navItems={[{ title: 'Лифты', to: urls.map }]}
            />
            <NavigationItem
              matchPrefixes={[urls.calendar]}
              minified={!state.navigationOpen}
              icon={<CalendarIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />}
              title="Календарь ТО"
              navItems={[{ title: 'Календарь ТО', to: urls.calendar }]}
            />
            {userInfo.isAdmin && (
              <NavigationItem
                matchPrefixes={[urls.dashboards]}
                minified={!state.navigationOpen}
                icon={<DashboardsIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />}
                title="Дашборды"
                navItems={[{ title: 'Дашборды', to: urls.dashboards }]}
              />
            )}
            {userInfo.organizationId && (
              <NavigationItem
                matchPrefixes={['/my-organization']}
                minified={!state.navigationOpen}
                icon={<MyOrganizationIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />}
                title="Организация"
                navItems={[{ title: 'Организация', to: urls.myOrganization }]}
              />
            )}
            <NavigationItem
              matchPrefixes={['/archive']}
              minified={!state.navigationOpen}
              icon={<SystemEventsIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />}
              title="История событий"
              navItems={[{ title: 'История событий', to: urls.systemEvents }]}
            />
            {isAdmin && (
              <NavigationItem
                matchPrefixes={[
                  '/settings/parameters',
                  '/settings/organizations',
                  '/settings/library',
                  '/processes',
                  '/settings/equipment',
                  '/schemas-and-data',
                ]}
                minified={!state.navigationOpen}
                icon={<SettingsIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />}
                title="Настройки"
                navItems={[
                  userInfo.isAdmin && { title: 'Организации', to: urls.organizations },
                  userInfo.isAdmin && { title: 'Переменные', to: urls.parameters },
                  userInfo.isAdmin && { title: 'Изображения', to: urls.documentsLibrary },
                  userInfo.isAdmin && { title: 'Оборудование', to: urls.equipmentSettings },
                  { title: 'MDM', to: urls.mdm },
                  { title: 'Модели оборудования', to: urls.equipmentModels },
                  { title: 'Техническое обслуживание', to: urls.maintenance },
                  { title: 'Сигнальные ситуации', to: urls.alarmSituations },
                ].filter(Boolean)}
              />
            )}
          </div>
          <div style={{ marginTop: 'auto' }}>
            <NavigationItem
              matchPrefixes={['/ai-assistant']}
              minified={!state.navigationOpen}
              icon={<AIIcon style={{ width: '16px', height: '16px', flexShrink: 0 }} />}
              title="AI Лифтер"
              navItems={[{ title: 'AI ассистент', to: urls.aiAssistant }]}
            />
          </div>
          <div className={css.navFooter}>
            <div className="flex-space-between gap8 center">
              {state.navigationOpen && <LiveClock />}
              <div style={{ marginLeft: 'auto' }}>
                <Button
                  size="default"
                  type="default"
                  onClick={() => setState((prev) => ({ ...prev, navigationOpen: !prev.navigationOpen }))}
                  icon={
                    <ArrowLeft
                      style={{
                        transition: 'all ease 0.3s',
                        transform: `rotate(${state.navigationOpen ? '0deg' : '180deg'})`,
                      }}
                    />
                  }
                />
              </div>
            </div>
          </div>
        </nav>
        <AccountSettingsSidePage
          open={openAccountSettingsSidePage}
          onClose={() => setOpenAccountSettingsSidePage(false)}
        />
        <div className={css.content}>{props.children}</div>
      </div>
    </div>
  )
}

export default MainLayout
